import { useEffect, useState, useCallback } from "react";
import {
  Users, Plane, CheckCircle, Clock, AlertTriangle, RefreshCw,
  ChevronRight, ChevronDown, ArrowRight, Activity, BarChart3,
  UserCheck, Circle, Loader, Shield, X
} from "lucide-react";
import { groundOpsApi } from "@/api";

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmtTime = iso => iso
  ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })
  : "—";

const STATUS_COLOR = {
  completed:   "text-green-400  bg-green-500/10  border-green-500/25",
  in_progress: "text-blue-400   bg-blue-500/10   border-blue-500/25",
  delayed:     "text-red-400    bg-red-500/10    border-red-500/30",
  blocked:     "text-red-400    bg-red-500/10    border-red-500/30",
  not_started: "text-slate-400  bg-slate-700/20  border-slate-600/20",
  available:   "text-green-400  bg-green-500/10  border-green-500/25",
};

function initials(name) {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function ProgressBar({ value, total, color = "bg-orange-500" }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const barColor = pct === 100 ? "bg-green-500" : pct > 50 ? "bg-amber-500" : color;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-slate-700/30 rounded-full h-1.5">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[9px] text-slate-500 w-8 text-right">{value}/{total}</span>
    </div>
  );
}

// ─── Phase 1: Allocation ─────────────────────────────────────────────────────
function AllocationView({ data, station, onRefresh, loading }) {
  const { flights = [], staff = [], summary = {} } = data;
  const [activeStaff, setActiveStaff] = useState(null);   // staff member being assigned
  const [assigning, setAssigning]     = useState(null);   // flightId being assigned

  async function handleAllocate(staffMember, flight) {
    setAssigning(flight.id);
    try {
      await groundOpsApi.allocateStaff(staffMember.name, {
        flight_id:  flight.id,
        role_label: staffMember.db_role,
      });
      await onRefresh();
      setActiveStaff(null);
    } finally { setAssigning(null); }
  }

  const unallocated = staff.filter(s => !s.is_allocated);
  const allocated   = staff.filter(s => s.is_allocated);

  return (
    <div className="space-y-5">
      {/* Progress banner */}
      <div className="bg-[#0c1929] rounded-xl border border-slate-700/30 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Shift Allocation Progress</div>
            <div className="text-2xl font-black text-white mt-1">
              {summary.allocated}
              <span className="text-slate-600 text-base font-normal"> / {summary.total_staff} staff allocated</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-slate-600 mb-1">{summary.flights_count} flights · {summary.covered_flights} covered</div>
            {summary.all_allocated
              ? <div className="flex items-center gap-1 text-green-400 text-[11px] font-bold justify-end"><CheckCircle size={12} /> All allocated</div>
              : <div className="text-amber-400 text-[11px] font-bold">{summary.unallocated} need assignment</div>
            }
          </div>
        </div>
        <ProgressBar value={summary.allocated} total={summary.total_staff} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* LEFT: Flights panel */}
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3 flex items-center gap-2">
            <Plane size={11} /> Today's Flights ({flights.length})
          </div>
          <div className="space-y-2">
            {flights.map(f => {
              const uncovered = f.unassigned_roles || [];
              return (
                <div key={f.id} className={`bg-[#0c1929] rounded-xl border p-4 ${f.fully_covered ? "border-green-500/20" : "border-slate-700/30"}`}
                  data-testid={`flight-card-${f.flight_number?.replace(/\s/g, "")}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-black text-white">{f.flight_number}</span>
                        <span className="text-[9px] text-slate-500">{f.aircraft_type}</span>
                        <span className={`text-[8px] font-bold uppercase border px-1.5 py-0.5 rounded ${f.fully_covered ? "bg-green-500/10 border-green-500/25 text-green-400" : "bg-amber-500/10 border-amber-500/25 text-amber-400"}`}>
                          {f.fully_covered ? "Covered" : `${uncovered.length} role${uncovered.length !== 1 ? "s" : ""} open`}
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {f.origin} → {f.destination} · Stand {f.stand}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] text-orange-400 font-bold">↓ {fmtTime(f.scheduled_arrival)}</div>
                      <div className="text-[10px] text-blue-400">↑ {fmtTime(f.scheduled_departure)}</div>
                    </div>
                  </div>
                  <ProgressBar value={f.task_summary?.completed || 0} total={f.task_summary?.total || 0} />
                  {uncovered.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {uncovered.map(r => (
                        <span key={r} className="text-[8px] text-red-400 border border-red-500/25 bg-red-500/8 px-1.5 py-0.5 rounded">
                          {r} unassigned
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: Staff panel */}
        <div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3 flex items-center gap-2">
            <Users size={11} /> Staff on Shift ({staff.length})
          </div>

          {/* Unallocated first */}
          {unallocated.length > 0 && (
            <div className="mb-3">
              <div className="text-[9px] text-amber-400 uppercase tracking-widest font-bold mb-2">Needs Allocation</div>
              <div className="space-y-2">
                {unallocated.map(s => {
                  const isActive = activeStaff?.id === s.id;
                  const myFlights = (s.available_flights || []).length > 0
                    ? s.available_flights
                    : flights; // if no perfect match, show all flights

                  return (
                    <div key={s.id} className="bg-[#0c1929] border border-amber-500/15 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setActiveStaff(isActive ? null : s)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/2 transition text-left"
                        data-testid={`staff-allocate-${s.id}`}
                      >
                        <div className="w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-[10px] font-black text-amber-400 flex-shrink-0">
                          {initials(s.name)}
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-sm text-white">{s.name}</div>
                          <div className="text-[10px] text-slate-500">{s.db_role} · {s.shift} shift</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] font-bold text-amber-400 border border-amber-500/30 bg-amber-500/10 px-2 py-1 rounded-full">Assign Flight</span>
                          <ChevronDown size={12} className={`text-slate-500 transition-transform ${isActive ? "rotate-180" : ""}`} />
                        </div>
                      </button>

                      {/* Flight picker */}
                      {isActive && (
                        <div className="border-t border-slate-700/30 px-4 py-3 bg-black/20">
                          <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-2">
                            Select a flight to assign {s.name}
                          </div>
                          <div className="space-y-1.5 max-h-52 overflow-y-auto">
                            {myFlights.map(f => (
                              <button
                                key={f.id}
                                onClick={() => handleAllocate(s, f)}
                                disabled={assigning === f.id}
                                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-[#0c1929] border border-slate-600/30 hover:border-orange-500/40 hover:bg-orange-500/5 transition text-left disabled:opacity-40"
                                data-testid={`pick-flight-${f.id?.slice(0, 6)}`}
                              >
                                <div>
                                  <div className="text-sm font-bold text-white">{f.flight_number}</div>
                                  <div className="text-[9px] text-slate-500">
                                    {f.origin} → {f.destination} · Stand {f.stand} · ↓{fmtTime(f.scheduled_arrival)}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {assigning === f.id
                                    ? <Loader size={13} className="animate-spin text-orange-400" />
                                    : <ArrowRight size={13} className="text-slate-600" />}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Allocated */}
          {allocated.length > 0 && (
            <div>
              <div className="text-[9px] text-green-400 uppercase tracking-widest font-bold mb-2">Allocated</div>
              <div className="space-y-1.5">
                {allocated.map(s => (
                  <div key={s.id}
                    className="flex items-center gap-3 px-4 py-3 bg-[#0c1929] border border-green-500/15 rounded-xl"
                    data-testid={`staff-allocated-${s.id}`}>
                    <div className="w-9 h-9 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center text-[10px] font-black text-green-400 flex-shrink-0">
                      {initials(s.name)}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-sm text-white">{s.name}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {s.db_role} · {s.allocated_flights.join(", ")}
                      </div>
                    </div>
                    <CheckCircle size={14} className="text-green-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Phase 2: Monitoring ─────────────────────────────────────────────────────
function MonitoringView({ data, onRefresh, loading }) {
  const { staff = [], flights = [] } = data;
  const flight_map = Object.fromEntries(flights.map(f => [f.flight_number, f]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-slate-500">Live task progress per staff member</p>
        <button onClick={onRefresh} className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-orange-400 transition">
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {staff.map(s => {
          const totalTasks = s.tasks_total;
          const done       = s.tasks_completed;
          const inProg     = s.tasks_in_progress;
          const delayed    = s.tasks_delayed;
          const pct        = totalTasks ? Math.round(done / totalTasks * 100) : 0;
          const statusKey  = delayed ? "delayed" : inProg ? "in_progress" : done === totalTasks && totalTasks > 0 ? "completed" : "not_started";

          return (
            <div key={s.id}
              className={`bg-[#0c1929] rounded-xl border p-4 ${STATUS_COLOR[statusKey]}`}
              data-testid={`monitor-staff-${s.id}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-slate-700/40 flex items-center justify-center text-[11px] font-black text-slate-300 flex-shrink-0">
                  {initials(s.name)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-white">{s.name}</span>
                    {delayed > 0 && <AlertTriangle size={12} className="text-red-400" />}
                  </div>
                  <div className="text-[10px] text-slate-500">{s.db_role}</div>
                  <div className="text-[10px] text-slate-600 mt-0.5">
                    {s.allocated_flights.length > 0 ? s.allocated_flights.join(", ") : "Not allocated"}
                  </div>
                </div>
                <span className={`text-[8px] font-black uppercase border px-2 py-1 rounded ${STATUS_COLOR[statusKey]}`}>
                  {statusKey.replace("_", " ")}
                </span>
              </div>

              <ProgressBar value={done} total={totalTasks} />

              <div className="mt-2 flex gap-3 text-[9px]">
                <span className="text-green-400">{done} done</span>
                {inProg > 0 && <span className="text-blue-400">{inProg} active</span>}
                {delayed > 0 && <span className="text-red-400">{delayed} delayed</span>}
                <span className="text-slate-600 ml-auto">{pct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Flight progress */}
      <div>
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3 flex items-center gap-2">
          <Plane size={11} /> Flight Progress
        </div>
        <div className="space-y-2">
          {flights.map(f => {
            const { completed = 0, total = 0, in_progress = 0 } = f.task_summary || {};
            return (
              <div key={f.id} className="bg-[#0c1929] border border-slate-700/30 rounded-xl px-4 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-sm text-white">{f.flight_number}</span>
                    <span className="text-[10px] text-slate-500">{f.origin}→{f.destination} · Stand {f.stand}</span>
                    <span className={`text-[8px] font-bold uppercase border px-1.5 py-0.5 rounded ${f.fully_covered ? "bg-green-500/10 border-green-500/25 text-green-400" : "bg-amber-500/10 border-amber-500/25 text-amber-400"}`}>
                      {f.ground_status?.replace("_", " ")}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500">↑ {fmtTime(f.scheduled_departure)}</div>
                </div>
                <ProgressBar value={completed} total={total} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Phase 3: Shift Report ────────────────────────────────────────────────────
function ShiftReportView({ data }) {
  const { staff = [], flights = [], summary = {} } = data;

  const totalTasks    = staff.reduce((s, m) => s + (m.tasks_total || 0), 0);
  const completedTasks = staff.reduce((s, m) => s + (m.tasks_completed || 0), 0);
  const delayedStaff  = staff.filter(s => s.tasks_delayed > 0);
  const onTimeFlights = flights.filter(f => f.fully_covered && (f.task_summary?.completed || 0) === (f.task_summary?.total || 0));

  function kpiColor(pct) {
    return pct >= 90 ? "text-green-400" : pct >= 70 ? "text-amber-400" : "text-red-400";
  }

  const completionPct = totalTasks ? Math.round(completedTasks / totalTasks * 100) : 0;
  const coveragePct   = summary.flights_count ? Math.round((summary.covered_flights || 0) / summary.flights_count * 100) : 0;

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Task Completion",    value: `${completionPct}%`, color: kpiColor(completionPct),  sub: `${completedTasks}/${totalTasks} tasks` },
          { label: "Flight Coverage",    value: `${coveragePct}%`,  color: kpiColor(coveragePct),    sub: `${summary.covered_flights}/${summary.flights_count} flights` },
          { label: "Staff Allocated",    value: `${summary.allocated}/${summary.total_staff}`, color: kpiColor(summary.allocated / summary.total_staff * 100), sub: "On-shift" },
          { label: "Staff with Delays",  value: delayedStaff.length, color: delayedStaff.length === 0 ? "text-green-400" : "text-red-400", sub: "Need attention" },
        ].map(k => (
          <div key={k.label} className="bg-[#0c1929] border border-slate-700/30 rounded-xl p-4">
            <div className="text-[9px] text-slate-500 uppercase tracking-widest font-bold mb-1">{k.label}</div>
            <div className={`text-2xl font-black ${k.color}`}>{k.value}</div>
            <div className="text-[10px] text-slate-600 mt-0.5">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Per-staff table */}
      <div>
        <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-3">Staff Performance</div>
        <div className="bg-[#0c1929] border border-slate-700/30 rounded-xl overflow-hidden">
          <div className="grid grid-cols-5 px-4 py-2.5 border-b border-slate-700/30 text-[9px] text-slate-500 uppercase font-bold tracking-widest">
            <span className="col-span-2">Staff</span>
            <span>Flights</span>
            <span>Tasks</span>
            <span>Status</span>
          </div>
          {staff.map(s => {
            const pct = s.tasks_total ? Math.round(s.tasks_completed / s.tasks_total * 100) : 0;
            const statusText = s.tasks_delayed > 0 ? "Delayed" : pct === 100 ? "Complete" : pct > 0 ? "In Progress" : "Not Started";
            const statusColor = s.tasks_delayed > 0 ? "text-red-400" : pct === 100 ? "text-green-400" : pct > 0 ? "text-blue-400" : "text-slate-500";
            return (
              <div key={s.id} className="grid grid-cols-5 px-4 py-3 border-b border-slate-700/20 last:border-0 hover:bg-white/2 transition" data-testid={`report-staff-${s.id}`}>
                <div className="col-span-2 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-slate-700/40 flex items-center justify-center text-[9px] font-black text-slate-400 flex-shrink-0">
                    {initials(s.name)}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{s.name}</div>
                    <div className="text-[9px] text-slate-600">{s.db_role}</div>
                  </div>
                </div>
                <div className="flex items-center text-[11px] text-slate-400 self-center">
                  {s.allocated_flights.join(", ") || "—"}
                </div>
                <div className="flex items-center self-center">
                  <div className="w-20">
                    <ProgressBar value={s.tasks_completed} total={s.tasks_total} />
                  </div>
                </div>
                <div className={`flex items-center text-[10px] font-bold self-center ${statusColor}`}>
                  {statusText}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const PHASES = [
  { id: "allocate", label: "1  Allocate",  icon: UserCheck  },
  { id: "monitor",  label: "2  Monitor",   icon: Activity   },
  { id: "report",   label: "3  Shift Report", icon: BarChart3 },
];

export default function GroundStaffPlanning() {
  const [phase, setPhase]   = useState("allocate");
  const [data, setData]     = useState({ flights: [], staff: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [station, setStation] = useState("DEL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await groundOpsApi.getShiftOverview(station);
      setData(d);
      // Auto-advance to monitor if all allocated
      if (d.summary?.all_allocated && phase === "allocate") setPhase("monitor");
    } catch {}
    finally { setLoading(false); }
  }, [station]);

  useEffect(() => { load(); }, [load]);

  const STATIONS = ["DEL", "BOM", "BLR", "HYD", "MAA", "CCU", "AMD", "PNQ"];

  return (
    <div className="p-5 space-y-5 min-h-full">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-black text-white flex items-center gap-2">
            <Shield size={18} className="text-orange-400" />
            Ground Staff Planning
          </h1>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={station}
            onChange={e => setStation(e.target.value)}
            className="bg-[#0c1929] border border-slate-600/30 rounded-lg px-3 py-2 text-white text-[11px] focus:outline-none focus:border-orange-500/40"
            data-testid="station-select"
          >
            {STATIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-orange-400 border border-slate-700/40 rounded-lg px-3 py-2 transition"
            data-testid="refresh-btn"
          >
            <RefreshCw size={11} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Phase tabs */}
      <div className="flex gap-1 bg-[#0c1929] p-1 rounded-xl border border-slate-700/30 w-fit">
        {PHASES.map(p => {
          const Icon = p.icon;
          const isActive = phase === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setPhase(p.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-[11px] font-bold transition-all ${
                isActive ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" : "text-slate-500 hover:text-slate-300"
              }`}
              data-testid={`phase-${p.id}`}
            >
              <Icon size={13} />
              {p.label}
            </button>
          );
        })}
      </div>

      {loading && <div className="text-center py-12 text-slate-600 animate-pulse">Loading shift data...</div>}

      {!loading && phase === "allocate" && (
        <AllocationView data={data} station={station} onRefresh={load} loading={loading} />
      )}
      {!loading && phase === "monitor" && (
        <MonitoringView data={data} onRefresh={load} loading={loading} />
      )}
      {!loading && phase === "report" && (
        <ShiftReportView data={data} />
      )}
    </div>
  );
}
